import { React, useEffect, useState } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  ShoppingCart, 
  ClipboardList, 
  Heart, 
  Layers, 
  Truck, 
  FileText, 
  Image as ImageIcon, 
  LogOut,
  ChevronRight,
  Users,
  Menu,
  X,
  Settings
} from 'lucide-react';

const AdminLayout = () => { 
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
      document.title = "Marvel Sato | Admin";
  }, []);

  useEffect(() => {
    setSidebarOpen(false);
  }, [location]);

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin-login');
  }; 

  const menuItems = [
    { name: 'Dashboard', path: '/admin', icon: <LayoutDashboard size={18} /> },
    { name: 'Customers', path: '/admin/customers', icon: <Users size={18} /> },
    { name: 'Bookings', path: '/admin/bookings', icon: <ShoppingCart size={18} /> },
    { name: 'Inquiries', path: '/admin/inquiries', icon: <ClipboardList size={18} /> },
    { name: 'Interested Customers', path: '/admin/interests', icon: <Heart size={18} /> },
    { name: 'Manage Categories', path: '/admin/categories', icon: <Layers size={18} /> },
    { name: 'Manage Products', path: '/admin/products', icon: <Truck size={18} /> },
    { name: 'Manage Blogs', path: '/admin/blogs', icon: <FileText size={18} /> },
    { name: 'Manage Gallery', path: '/admin/gallery', icon: <ImageIcon size={18} /> },
    { name: 'Settings', path: '/admin/settings', icon: <Settings size={18} /> }
  ];

  return (
    <div className="flex h-screen bg-slate-100 font-sans overflow-hidden">
      
      {/* --- MOBILE OVERLAY --- */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* --- SIDEBAR --- */}
      <aside className={`
        fixed inset-y-0 left-0 w-72 bg-slate-900 text-white flex flex-col shadow-2xl z-40 
        transform transition-transform duration-300 ease-in-out lg:relative lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="p-5 border-b border-slate-800 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-black italic tracking-tighter uppercase">
              SATO <span className="text-orange-500 not-italic">ADMIN</span>
            </h1>
            <p className="text-[10px] text-slate-500 font-bold uppercase tracking-[0.3em] mt-1">Fleet Management</p>
          </div>
          <button className="lg:hidden text-slate-400" onClick={() => setSidebarOpen(false)}>
            <X size={20} />
          </button>
        </div>

        {/* Added no-scrollbar here */}
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1 no-scrollbar">
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center justify-between px-4 py-3 rounded-xl transition-all duration-200 group ${
                  isActive 
                    ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/20' 
                    : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                }`}
              >
                <div className="flex items-center gap-3 font-bold text-[11px] uppercase tracking-wider">
                  {item.icon}
                  {item.name}
                </div>
                {isActive && <ChevronRight size={14} />}
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-slate-800">
          <button 
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-xl font-black text-[10px] uppercase tracking-widest text-red-400 border border-red-400/20 hover:bg-red-500 hover:text-white hover:border-red-500 transition-all duration-300"
          >
            <LogOut size={14} />
            Log Out
          </button>
        </div>
      </aside>

      {/* --- MAIN CONTENT --- */}
      <main className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <header className="h-16 md:h-20 bg-white border-b border-slate-200 flex items-center justify-between px-4 md:px-10 shrink-0">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 text-slate-600 hover:bg-slate-100 rounded-lg"
            >
              <Menu size={24} />
            </button>
            <h2 className="text-[10px] md:text-sm font-black uppercase tracking-[0.2em] text-slate-400">
              <span className="text-slate-900">
                {menuItems.find(i => i.path === location.pathname)?.name || "Overview"}
              </span>
            </h2>
          </div>
          
          <div className="flex items-center gap-2 md:gap-4 text-slate-900 font-bold text-[10px] md:text-xs">
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
            <span className="hidden sm:inline">System Live</span>
          </div>
        </header>

        {/* Added no-scrollbar here */}
        <section className="flex-1 overflow-y-auto p-4 md:p-10 bg-[#f8fafc] no-scrollbar">
          <div className="max-w-7xl mx-auto">
            <Outlet />
          </div>
        </section>
      </main>
    </div>
  );
};

export default AdminLayout;